import React, { useState } from "react";
import { Button } from "@src/lib/ui/Button";
import { Textarea } from "@src/lib/ui/Textarea";
import ThumbsUpOutlineIcon from "@src/lib/icons/18px/thumbs-up-outline";
import ThumbsDownOutlineIcon from "@src/lib/icons/18px/thumbs-down-outline";
import { FeedbackSelection, CreateAiFeedbackInput } from "@src/types.generated";
import { useCreateAiFeedbackMutation } from "@src/features/libraryItem/course/build/settingsPane/AIAssistCourseSettings.generated";
import Text from "@src/lib/ui/Text";
import { useAIFeedbackTranslations } from "./hooks/useAIFeedbackTranslations";

interface AIThumbsUpThumbsDownFeedbackFormProps {
  feedbackInput: Omit<CreateAiFeedbackInput, "selection" | "text">;
  showPromptText?: boolean;
  promptText?: string;
  positiveFeedbackPrompt?: string;
  negativeFeedbackPrompt?: string;
  onSubmit?: () => void;
  hideAfterSubmit?: boolean;
}

export const AIThumbsUpThumbsDownFeedbackForm: React.FC<
  AIThumbsUpThumbsDownFeedbackFormProps
> = ({
  feedbackInput,
  showPromptText = true,
  promptText,
  positiveFeedbackPrompt,
  negativeFeedbackPrompt,
  onSubmit,
  hideAfterSubmit = true,
}) => {
  const [mutation] = useCreateAiFeedbackMutation();
  const [feedbackType, setFeedbackType] = useState<FeedbackSelection | null>(
    null,
  );
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const translations = useAIFeedbackTranslations();

  // Use translations as defaults if props not provided
  const displayPromptText = promptText || translations.howDidIDo;
  const displayPositiveFeedbackPrompt =
    positiveFeedbackPrompt || translations.greatTellMeWhatYouLiked;
  const displayNegativeFeedbackPrompt =
    negativeFeedbackPrompt || translations.helpMeImprove;

  if (hideAfterSubmit && isSubmitted) {
    return null;
  }

  const handleFeedback = (type: FeedbackSelection) => {
    setFeedbackType((prevType) => (prevType === type ? null : type));
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackType || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await mutation({
        variables: {
          input: {
            ...feedbackInput,
            selection: feedbackType,
            text: feedbackText || undefined,
          },
        },
      });
      setIsSubmitted(true);
      onSubmit?.();
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-row items-center justify-between gap-2">
        {showPromptText && (
          <Text variant="compact-description" className="text-gray-6">
            {displayPromptText}
          </Text>
        )}
        <div className="flex gap-1.5">
          <Button
            variant={
              feedbackType === FeedbackSelection.Positive ? "default" : "ghost"
            }
            size="icon-xs"
            onClick={() => handleFeedback(FeedbackSelection.Positive)}
            disabled={isSubmitting}
          >
            <ThumbsUpOutlineIcon className="size-3.5" />
          </Button>
          <Button
            variant={
              feedbackType === FeedbackSelection.Negative ? "default" : "ghost"
            }
            size="icon-xs"
            onClick={() => handleFeedback(FeedbackSelection.Negative)}
            disabled={isSubmitting}
          >
            <ThumbsDownOutlineIcon className="size-3.5" />
          </Button>
        </div>
      </div>

      {feedbackType !== null && (
        <div className="space-y-2">
          <Text variant="compact-description">
            {feedbackType === FeedbackSelection.Positive
              ? displayPositiveFeedbackPrompt
              : displayNegativeFeedbackPrompt}
          </Text>
          <Textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={
              feedbackType === FeedbackSelection.Positive
                ? translations.placeholderPositiveFeedback
                : translations.placeholderNegativeFeedback
            }
            className="min-h-[80px] text-sm"
          />
          <div className="flex justify-end">
            <Button
              variant="default"
              size="sm"
              onClick={handleSubmitFeedback}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? translations.submitting
                : translations.submitFeedback}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
